<script setup lang="ts">
  import type { ScrollAreaScrollbarProps } from 'reka-ui'
  import type { HTMLAttributes } from 'vue'
  import { reactiveOmit } from '@vueuse/core'
  import { ScrollAreaScrollbar, ScrollAreaThumb } from 'reka-ui'
  import { cva, type VariantProps } from 'class-variance-authority'
  import { cn } from '@rapoo/element/lib/utils'

  const scrollbarVariants = cva('flex touch-none select-none transition-colors', {
    variants: {
      orientation: {
        vertical: 'h-full border-l border-l-transparent',
        horizontal: 'h-2.5 flex-col border-t border-t-transparent',
      },
      size: {
        sm: '',
        md: '',
        lg: '',
      },
    },
    compoundVariants: [
      { orientation: 'vertical', size: 'sm', class: 'w-1' },
      { orientation: 'vertical', size: 'md', class: 'w-1.5' },
      { orientation: 'vertical', size: 'lg', class: 'w-2' },
      { orientation: 'horizontal', size: 'sm', class: 'h-1' },
      { orientation: 'horizontal', size: 'md', class: 'h-1.5' },
      { orientation: 'horizontal', size: 'lg', class: 'h-2' },
    ],
    defaultVariants: {
      orientation: 'vertical',
      size: 'md',
    },
  })

  interface Props extends ScrollAreaScrollbarProps {
    class?: HTMLAttributes['class']
    thumbClass?: HTMLAttributes['class']
    size?: VariantProps<typeof scrollbarVariants>['size']
  }

  const props = withDefaults(defineProps<Props>(), {
    orientation: 'vertical',
    size: 'md',
  })

  const delegatedProps = reactiveOmit(props, 'class', 'thumbClass', 'size')
</script>

<template>
  <ScrollAreaScrollbar
    v-bind="delegatedProps"
    class="bg-transparent"
    :class="cn(scrollbarVariants({ orientation, size }), props.class)"
  >
    <ScrollAreaThumb :class="cn('relative flex-1 rounded-full bg-n-light-default', thumbClass)" />
  </ScrollAreaScrollbar>
</template>
